import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { MARKET_IDS, type MarketId, type MarketQuote } from '@omnia/shared';
import { tr } from '../../lib/i18n.ts';
import { useShows } from '../BarPlanContext.tsx';
import { useInfoBarPrefs } from '../prefsStore.ts';
import { useMarkets } from '../queries.ts';
import { MARKET_LABELS, directionOf, formatChange, formatMarketValue } from '../markets.ts';
import { StaleMark } from './StaleMark.tsx';
import type { WidgetVariant } from './types.ts';

const EXTRA_MARKETS: Partial<Record<MarketId, true>> = { ceyrek: true, yarim: true, tam: true, cumhuriyet: true, ayar22: true };
const ARROWS = { up: ArrowUp, down: ArrowDown, flat: Minus };

export function MarketsWidget({ variant }: { variant: WidgetVariant }) {
  const { markets } = useInfoBarPrefs();
  const query = useMarkets(true);
  const quotes = query.data?.items.filter((item) => markets.includes(item.id)) ?? [];
  quotes.sort((a, b) => MARKET_IDS.indexOf(a.id) - MARKET_IDS.indexOf(b.id));
  const shows = useShows();
  const shown = variant === 'sheet' ? quotes : quotes.filter((quote) => shows(quote.id in EXTRA_MARKETS ? 'extras' : (quote.id as 'usd')));
  if (variant === 'bar' && quotes.length > 0 && shown.length === 0) return null;

  return (
    <div className="iw iw-markets" data-variant={variant}>
      {shown.length > 0 ? (
        <ul className="iw-tickers" aria-label={tr.infobar.widget.markets}>
          {shown.map((quote) => (
            <Ticker key={quote.id} quote={quote} stale={query.stale || quote.stale === true} />
          ))}
        </ul>
      ) : query.isError ? (
        <button type="button" className="iw-retry" onClick={() => query.refetch()}>
          {tr.infobar.marketsUnavailable} · {tr.common.retry}
        </button>
      ) : query.data ? null : (
        <span className="iw-loading" aria-busy="true">
          {tr.infobar.loadingShort}
        </span>
      )}
    </div>
  );
}

function Ticker({ quote, stale }: { quote: MarketQuote; stale: boolean }) {
  const direction = directionOf(quote.changePct);
  const Arrow = ARROWS[direction];
  const label = MARKET_LABELS[quote.id];
  const change = quote.changePct === null ? null : formatChange(quote.changePct);
  return (
    <li
      className="iw-ticker"
      data-market={quote.id}
      data-direction={direction}
      data-stale={stale || undefined}
      data-extra={quote.id in EXTRA_MARKETS || undefined}
      data-piece={quote.id in EXTRA_MARKETS ? 'extras' : quote.id}
      title={`${label.long} · ${tr.infobar.source(quote.source, quote.asOf)}${change ? ` · ${tr.infobar.change24h}: ${change}` : ''}${stale ? ` · ${tr.infobar.stale}` : ''}`}
    >
      <span className="iw-ticker-name">{label.short}</span>
      <span className="iw-ticker-value">
        {quote.currency === 'USD' && '$'}
        {formatMarketValue(quote)}
      </span>
      {change && (
        <span className="iw-ticker-change">
          <Arrow size={11} aria-hidden />
          <span>{change}</span>
          <span className="visually-hidden"> ({tr.infobar[direction]})</span>
        </span>
      )}
      {stale && <StaleMark />}
    </li>
  );
}
